<script setup lang="ts">
  import { useApi } from '@/composables/use-api.ts';
  import { CompanyCalendarDayDto } from '@/services/api/calendar-day-api.ts';
  import { loadWithStatusAndErrorHandleAsync } from '@/services/loading-svc.ts';
  import { useCalendarDayStore } from '@/stores/calendar-day.ts';
  import { useNeoDotStore } from '@/stores/neo-dot';
  import { useThemeStore } from '@/stores/theme';
  import { secondsToHoursAndMinutesSimple } from '@/utils/item-printer.ts';
  import dayjs from 'dayjs';
  import { Calendar } from 'v-calendar';
  import type {
    CalendarDay,
    Page,
  } from 'v-calendar/dist/types/src/utils/page.js';
  import { computed, ref } from 'vue';

  const emit = defineEmits<{
    (e: 'on-change-month', yearMonth: string): void;
    (e: 'on-day-click', date: dayjs.Dayjs): void;
  }>();

  const { attendance } = useApi();
  const calendarDayStore = useCalendarDayStore();
  const neoDotStore = useNeoDotStore();
  const themeStore = useThemeStore();

  const nowDate = ref<dayjs.Dayjs>(dayjs(dayjs().format('YYYY-MM-DD')));
  const page = ref<Page>();
  const loading = ref(false);
  const dateAttendanceSet = ref(new Set<string>());
  const dateHolidaySet = ref(new Set<string>());
  const dateNeoDotSet = ref(new Set<string>());
  const dateVacationSet = ref(new Set<string>());
  const dateExtraSet = ref(new Set<string>());
  const dateNightSet = ref(new Set<string>());
  const dateWorkMap = ref(new Map<string, number>());
  const dateMissingAttendanceMap = ref(new Map<string, string>());
  const dateMissingApplicationMap = ref(new Map<string, string>());
  const savedYearMonth = ref<string>();

  const attributes = computed(() => {
    return [
      {
        key: 'now',
        dates: [calendarDayStore.selectDate],
      },
    ];
  });

  async function refreshAsync(yearMonth: string) {
    if (savedYearMonth.value == yearMonth) return;

    savedYearMonth.value = yearMonth;

    await loadWithStatusAndErrorHandleAsync(
      (status) => {
        loading.value = status;
      },
      async () => {
        (await calendarDayStore.listAsync(yearMonth)).forEach(
          (item: CompanyCalendarDayDto) => {
            if (item.holiday) dateHolidaySet.value.add(item.date);
            if (item.neoDot) dateNeoDotSet.value.add(item.date);
          }
        );

        (await attendance.day.listAsync(yearMonth)).forEach((item) => {
          dateAttendanceSet.value.add(item.date);
          if (item.vacationType || item.vacationName)
            dateVacationSet.value.add(item.date);
          if (
            item.paidWorkContractExtraDayTime ||
            item.paidWorkContractExtraNightTime ||
            item.paidWorkStatutoryExtraDayTime ||
            item.paidWorkStatutoryExtraNightTime
          )
            dateExtraSet.value.add(item.date);
          if (
            item.paidWorkNightTime ||
            item.paidWorkContractExtraNightTime ||
            item.paidWorkStatutoryExtraNightTime ||
            item.paidWorkHolidayMorningNightTime ||
            item.paidWorkHolidayAfterNightTime
          )
            dateNightSet.value.add(item.date);
          if (
            item.paidWorkTime ||
            item.vacationType ||
            item.vacationName ||
            item.workStartDt
          )
            dateWorkMap.value.set(item.date, item.paidWorkTime);
        });

        (await attendance.day.listMissingAttendancesAsync(yearMonth))
          .filter((item) => !dayjs(item.date).isAfter(nowDate.value))
          .forEach((item) => {
            if (item.type == '근무시작/종료')
              dateMissingAttendanceMap.value.set(item.date, '출퇴근');
            else if (item.type == '근무종료')
              dateMissingAttendanceMap.value.set(item.date, '퇴근');
          });

        (await attendance.day.listMissingApplicationsAsync(yearMonth)).forEach(
          (item) => {
            if (item.type == '야간 근로 신청')
              dateMissingApplicationMap.value.set(item.date, '야간신청');
            else if (item.type == '연장 근로 신청')
              dateMissingApplicationMap.value.set(item.date, '연장신청');
            else if (item.type == '휴일 근로 신청')
              dateMissingApplicationMap.value.set(item.date, '휴일신청');
          }
        );
      }
    );
  }

  function onDayClick(day: CalendarDay) {
    if (dateNeoDotSet.value.has(day.id)) {
      neoDotStore.togglePopup();
    }

    calendarDayStore.selectDate = day.noonDate;
    const date = dayjs(day.id);
    if (
      date.isAfter(nowDate.value) &&
      !dateAttendanceSet.value.has(date.format('YYYY-MM-DD'))
    )
      return;
    // if (!dateAttendanceSet.value.has(date.format('YYYY-MM-DD'))) return;
    emit('on-day-click', date);
  }

  function updatePage(pages: Page[]) {
    if (!pages) return;
    if (page.value == pages[0]) return;

    page.value = pages[0];
    refreshAsync(page.value.id);
    emit('on-change-month', page.value.id);
  }
</script>

<template>
  <div class="flex justify-center items-center">
    <Calendar
      :is-dark="themeStore.isDark"
      :is-loading="loading"
      :style="{
        width: '100%',
        backgroundColor: themeStore.isDark ? '#2A323C' : '#fff',
      }"
      :attributes="attributes"
      :min-date="new Date('2023-10-01')"
      :max-date="new Date()"
      :trim-weeks="true"
      :masks="{ title: 'YYYY년 M월' }"
      @update:pages="updatePage">
      <template #header-title="{ title }">
        <div>
          {{ title }}
        </div>
      </template>
      <template #day-content="{ day }">
        <div class="vc-day vc-day-box-center-center flex-col c-day">
          <div
            class="vc-day-content vc-focus vc-attr vc-disabled vc-attr"
            :class="{ 'neo-dot': dateNeoDotSet.has(day.id) }"
            tabindex="-1"
            role="button"
            @click="onDayClick(day)">
            <!-- 일요일(휴일) -->
            <div
              v-if="dateHolidaySet.has(day.id)"
              class="vc-attr vc-content vc-red">
              {{ day.day }}
            </div>
            <!-- 네오닷 -->
            <div
              v-else-if="dateNeoDotSet.has(day.id)"
              class="vc-attr vc-content vc-yellow c-neo-dot-accent">
              {{ day.day }}
            </div>
            <!-- 토요일 -->
            <div
              v-else-if="dayjs(day.id).day() == 6"
              class="vc-attr vc-content vc-blue">
              {{ day.day }}
            </div>
            <!-- 일하는 날 -->
            <div
              v-else-if="dateWorkMap.has(day.id)"
              class="vc-attr vc-content vc-gray">
              {{ day.day }}
            </div>
            <!-- 오늘 -->
            <div
              v-else-if="dayjs(day.id).isAfter(nowDate)"
              class="vc-attr vc-content c-next-day">
              {{ day.day }}
            </div>
            <!-- 다음달 날짜? -->
            <div
              v-else
              class="vc-attr vc-content vc-gray">
              {{ day.day }}
            </div>
          </div>
          <div class="vc-day-layer vc-day-box-center-center mb-1">
            <div class="vc-dots">
              <span
                v-if="dateVacationSet.has(day.id)"
                class="vc-dot vc-dot-start vc-purple vc-attr" />

              <span
                v-if="dateNightSet.has(day.id)"
                class="vc-dot vc-dot-start vc-orange vc-attr" />

              <span
                v-if="dateExtraSet.has(day.id)"
                class="vc-dot vc-dot-start vc-green vc-attr" />
            </div>
          </div>
          <div
            v-if="dateMissingAttendanceMap.has(day.id)"
            class="vc-day-box-center-bottom c-work-time-warn-1">
            {{ dateMissingAttendanceMap.get(day.id) }}
          </div>

          <template v-else-if="dateMissingApplicationMap.has(day.id)">
            <div
              v-if="dateWorkMap.get(day.id) == 0"
              class="vc-day-box-center-bottom c-work-time-warn-2">
              {{ dateMissingApplicationMap.get(day.id) }}
            </div>
            <div
              v-else
              class="vc-day-box-center-bottom c-work-time-warn-2">
              {{ secondsToHoursAndMinutesSimple(dateWorkMap.get(day.id)!) }}
            </div>
          </template>
          <div
            v-else-if="dateWorkMap.get(day.id)"
            class="vc-day-box-center-bottom c-work-time">
            {{ secondsToHoursAndMinutesSimple(dateWorkMap.get(day.id)!) }}
          </div>
          <div
            v-else-if="dateNeoDotSet.has(day.id)"
            class="vc-yellow c-neo-dot-accent"
            :style="{
              fontSize: '0.65rem',
              letterSpacing: '-0.02rem',
              whiteSpace: 'nowrap',
            }">
            Neo-dot
          </div>
        </div>
      </template>
      <template #footer>
        <div class="w-full pb-3">
          <div class="flex justify-center">
            <div class="text-sm">
              <div
                class="badge badge-xs"
                style="border-color: #9333ea; background-color: #9333ea" />
              휴가
            </div>
            <div class="text-sm ml-3">
              <div
                class="badge badge-xs"
                style="border-color: #ea580c; background-color: #ea580c" />
              야간
            </div>
            <div class="text-sm ml-3">
              <div
                class="badge badge-xs"
                style="border-color: #16a34a; background-color: #16a34a" />
              연장
            </div>

            <div
              class="badge badge-xs text-white font-bold py-2 ml-2"
              style="border-color: #d97706; background-color: #d97706">
              퇴근 누락
            </div>

            <div
              class="badge badge-xs text-white font-bold py-2 ml-2"
              style="border-color: #f000b8; background-color: #f000b8">
              신청서 누락
            </div>
          </div>
        </div>
      </template>
    </Calendar>

    <div class="absolute">
      <span
        v-if="loading"
        class="loading loading-ring loading-lg" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .vc-gray {
    [data-theme='dark'] & {
      color: #999 !important;
    }
  }

  .c-day {
    height: 52px;
    justify-content: start !important;

    .c-next-day {
      color: #94a3b8;
      [data-theme='dark'] & {
        color: #777;
      }
    }

    .c-work-time {
      color: var(--vc-day-content-disabled-color);
      font-size: var(--vc-text-sm);
      font-weight: var(--vc-font-medium);
      line-height: 1rem;
    }

    .c-work-time-warn-1 {
      color: #d97706;
      font-size: 13px;
      font-weight: var(--vc-font-medium);
      line-height: 1rem;
    }

    .c-work-time-warn-2 {
      color: #f000b8;
      font-size: 13px;
      font-weight: var(--vc-font-medium);
      line-height: 1rem;
    }

    .c-neo-dot-accent {
      color: var(--vc-accent-400);
    }
  }
</style>
